import React, { useState, useEffect, memo } from 'react';
import {
    Typography,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    DialogActions,
    Box,
    Paper,
    Grid,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    TextField,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip
} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import eventsApi from '../../../api/eventsApi/eventsApi';
import { useSelector } from 'react-redux';

interface QuestionType {
    id: string;
    name: string;
}

interface QuestionCategory {
    id: string;
    name: string;
    isDisabled: boolean;
    index: number;
    questionType: QuestionType;
}

interface Question {
    id: string;
    text: string;
    isDisabled: boolean;
    userId: string | null;
    index: number;
    questionCategory: QuestionCategory;
    answers: PinQuestionDetailsAnswer[];
}

interface PinQuestionsResponse {
    questions: Question[];
}

interface PinQuestionDetailsAnswer {
    id: string;
    yesNo: boolean;
    description: string | null;
    findings: string;
    proposedSolutions: string;
    fileAttachments: {
        id: string;
        fileName: string;
    }[];
    pinId: string;
}

interface PinQuestion {
    id: string;
    text: string;
    isDisabled: boolean;
    userId: string | null;
    index: number;
    questionCategory: QuestionCategory;
    answers: PinQuestionDetailsAnswer[];
}

interface PinQuestionsAndAnswersResponse {
    questions: PinQuestion[];
}

interface Answer {
    answer: string;
    description: string | null;
    findings: string | null;
    proposedSolutions: string | null;
    locked?: boolean;
    files?: File[];
    questionAnswerId?: string;
    fileAttachments?: {
        id: string;
        fileName: string;
    }[];  // Existing file attachments from the server
    fileAttachmentIdsToRemove?: string[];  // IDs of files to be removed
}

const defaultAnswer: Answer = {
    answer: '',
    description: null,
    findings: null,
    proposedSolutions: null
};

interface FileAttachment {
    id: string;
    fileName: string;
}

interface FormAnswers {
    [key: string]: Answer;
}

interface ApiResponse<T> {
    items: T[];
}

interface RiskTableData {
    probabilityLevel: string;
    impactLevel: string;
    threatLevel: string;
    measureCoefficient: string;
    residualRiskLevel: string;
}

interface QuestionAnswer {
    id: string;
    questionId: string;
    userId: string;
    yesNo: boolean;
    findings: string;
    pinId: string;
    proposedSolutions: string;
    description: string;
    files?: File[];
    fileAttachmentIdsToRemove?: string[];  // IDs of files to be removed
}

interface QuestionAnswerResponse {
    id: string;
    questionId: string;
    userId: string;
    yesNo: boolean;
    findings: string;
    pinId: string;
    proposedSolutions: string;
    description: string;
}

interface ChecklistModalProps {
    open: boolean;
    onClose: () => void;
    pinId: string;
}

interface AudioRecordingState {
    isRecording: boolean;
    mediaRecorder: MediaRecorder | null;
}

interface CategoryRiskAnswer {
    questionCategoryId: string;
    pinId: string;
    userId: string;
    probabilityLevel: number;
    impactLevel: number;
    threatLevel: number;
    appliedMeasureCoefficient: number;
    residualRiskLevel: number;
    isAfterMeasures: boolean;
}

interface CategoryRiskAnswerUpdate extends CategoryRiskAnswer {
    id: string;
}

interface CategoryRiskAnswerResponse extends CategoryRiskAnswerUpdate {
    // Eğer API'den dönen başka alanlar varsa buraya eklenebilir
}

interface QuestionCreate {
    text: string;
    userId: string;
    questionCategoryId: string;
    pinId: string;
}

interface QuestionCategoryCreate {
    name: string;
    userId: string;
    questionTypeId: string;
    pinId: string;
}

interface CategoryResponse {
    id: string;
    name: string;
    userId: string;
    questionTypeId: string;
}

interface QuestionAnswerUpdate {
    id: string;
    questionId: string;
    userId: string;
    yesNo: boolean;
    findings: string;
    pinId: string;
    proposedSolutions: string;
    description: string;
    files?: File[];
    fileAttachmentIdsToRemove?: string[];
}

const CategoryBox = memo(({
    name,
    onClick,
    questionCount,
    answeredCount,
    canDelete,
    onDelete
}: {
    name: string;
    onClick: () => void;
    questionCount: number;
    answeredCount: number;
    canDelete?: boolean;
    onDelete?: () => void;
}) => {
    const isAllAnswered = answeredCount === questionCount;

    return (
        <Paper
            elevation={2}
            sx={{
                p: 2,
                height: '100px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
                transition: 'background-color 0.3s',
                position: 'relative'
            }}
            onClick={onClick}
        >
            {canDelete && onDelete && (
                <Tooltip title="Kategoriyi sil" placement="top">
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            onDelete();
                        }}
                        sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            color: '#b71c1c',
                            backgroundColor: 'rgba(255,255,255,0.8)',
                            '&:hover': { backgroundColor: 'rgba(255,255,255,1)' }
                        }}
                    >
                        <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            )}
            <Typography
                sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    fontStyle: 'italic',
                    color: isAllAnswered ? '#2e7d32' : '#666',
                    fontWeight: isAllAnswered ? 700 : 400,
                }}
            >
                {answeredCount}/{questionCount}
            </Typography>
            <Typography
                variant="subtitle1"
                align="center"
                sx={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#333'
                }}
            >
                {name}
            </Typography>
        </Paper>
    );
});

const RiskTable = memo(({
    data,
    onChange,
    backgroundColor,
    isLocked,
    isEditing,
    onEdit,
    onSave,
    isAfterMeasures
}: {
    data: RiskTableData;
    onChange: (field: keyof RiskTableData, value: string) => void;
    backgroundColor: string;
    isLocked?: boolean;
    isEditing?: boolean;
    onEdit?: () => void;
    onSave?: () => void;
    isAfterMeasures: boolean;
}) => {
    const calculateThreatLevel = () => {
        const probability = parseFloat(data.probabilityLevel) || 0;
        const impact = parseFloat(data.impactLevel) || 0;
        return (probability * impact).toString();
    };

    const calculateResidualRisk = () => {
        const threat = parseFloat(calculateThreatLevel()) || 0;
        const coefficient = parseFloat(data.measureCoefficient) || 0;
        return (threat * coefficient).toString();
    };

    const getRiskLevel = () => {
        const residualRisk = parseFloat(calculateResidualRisk()) || 0;

        // Yeni skala: DÜŞÜK r<3 | ORTA 3≤r≤10 | YÜKSEK 10<r<20 | KRİTİK r≥20
        if (residualRisk < 3) {
            return { text: 'DÜŞÜK', bgColor: '#e0e0e0', textColor: '#000000' };
        } else if (residualRisk <= 10) {
            return { text: 'ORTA', bgColor: '#fff176', textColor: '#000000' };
        } else if (residualRisk < 20) {
            return { text: 'YÜKSEK', bgColor: '#ffb74d', textColor: '#000000' };
        } else {
            return { text: 'KRİTİK', bgColor: '#ef5350', textColor: '#FFFFFF' };
        }
    };

    const riskLevel = getRiskLevel();

    return (
        <Box>
            {isLocked && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                    <Button
                        onClick={isEditing ? onSave : onEdit}
                        sx={{
                            color: isEditing ? '#fff' : '#094151',
                            backgroundColor: isEditing ? '#094151' : 'transparent',
                            '&:hover': {
                                backgroundColor: isEditing ? '#022631' : 'rgba(9, 65, 81, 0.08)'
                            }
                        }}
                    >
                        {isEditing ? 'Güncelle' : 'Düzenle'}
                    </Button>
                </Box>
            )}
            <TableContainer component={Paper} sx={{ mb: 2, backgroundColor }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Olasılık Seviyesi</TableCell>
                            <TableCell>Etki Seviyesi</TableCell>
                            <TableCell>Tehdit Seviyesi</TableCell>
                            <TableCell>Uygulanan Tedbirler Kat Sayısı</TableCell>
                            <TableCell>Kalıntı Risk Seviyesi</TableCell>
                            <TableCell>Risk ve Tehdit Seviyesi</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell>
                                <TextField
                                    size="small"
                                    type="number"
                                    inputProps={{
                                        min: 0,
                                        max: 100,
                                        step: "1"
                                    }}
                                    value={data.probabilityLevel}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        const numValue = Number(value);
                                        if (value === '' || (numValue >= 0 && numValue <= 100)) {
                                            onChange('probabilityLevel', value);
                                        }
                                    }}
                                    sx={{ width: '100px' }}
                                    disabled={isLocked && !isEditing}
                                />
                            </TableCell>
                            <TableCell>
                                <TextField
                                    size="small"
                                    type="number"
                                    inputProps={{
                                        min: 0,
                                        max: 100,
                                        step: "1"
                                    }}
                                    value={data.impactLevel}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        const numValue = Number(value);
                                        if (value === '' || (numValue >= 0 && numValue <= 100)) {
                                            onChange('impactLevel', value);
                                        }
                                    }}
                                    sx={{ width: '100px' }}
                                    disabled={isLocked && !isEditing}
                                />
                            </TableCell>
                            <TableCell>
                                <Typography>{calculateThreatLevel()}</Typography>
                            </TableCell>
                            <TableCell>
                                <TextField
                                    size="small"
                                    type="number"
                                    inputProps={{
                                        min: 0,
                                        max: 100,
                                        step: "1"
                                    }}
                                    value={data.measureCoefficient}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        const numValue = Number(value);
                                        if (value === '' || (numValue >= 0 && numValue <= 100)) {
                                            onChange('measureCoefficient', value);
                                        }
                                    }}
                                    sx={{ width: '100px' }}
                                    disabled={isLocked && !isEditing}
                                />
                            </TableCell>
                            <TableCell>
                                <Typography>{calculateResidualRisk()}</Typography>
                            </TableCell>
                            <TableCell>
                                <Typography sx={{
                                    backgroundColor: riskLevel.bgColor,
                                    color: riskLevel.textColor,
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    display: 'inline-block',
                                    fontWeight: 'bold'
                                }}>
                                    {riskLevel.text}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
});

const MicrophoneButton = memo(({
    onRecordingComplete,
    onTranscriptionComplete,
    disabled = false
}: {
    onRecordingComplete: (audioBlob: Blob) => void;
    onTranscriptionComplete: (text: string) => void;
    disabled?: boolean;
}) => {
    const [recordingState, setRecordingState] = useState<AudioRecordingState>({
        isRecording: false,
        mediaRecorder: null
    });
    const [isProcessing, setIsProcessing] = useState(false);

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mediaRecorder = new MediaRecorder(stream);
            const audioChunks: BlobPart[] = [];

            mediaRecorder.ondataavailable = (event) => {
                audioChunks.push(event.data);
            };

            mediaRecorder.onstop = async () => {
                const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });
                onRecordingComplete(audioBlob);

                // Stop all tracks in the stream
                stream.getTracks().forEach(track => track.stop());

                // Process the audio with our API
                try {
                    setIsProcessing(true);
                    const response = await eventsApi.getSpeechToText(audioBlob);
                    // Parse the response text to get only the text content
                    try {
                        const parsedResponse = JSON.parse(response.transcription);
                        onTranscriptionComplete(parsedResponse.text);
                    } catch (error) {
                        // If parsing fails, use the raw text
                        onTranscriptionComplete(response.transcription);
                    }
                } catch (error) {
                    console.error('Transcription error:', error);
                } finally {
                    setIsProcessing(false);
                }
            };

            mediaRecorder.start();
            setRecordingState({ isRecording: true, mediaRecorder });
        } catch (error) {
            console.error('Error accessing microphone:', error);
        }
    };

    const stopRecording = () => {
        if (recordingState.mediaRecorder) {
            recordingState.mediaRecorder.stop();
            setRecordingState({ isRecording: false, mediaRecorder: null });
        }
    };

    return (
        <IconButton
            size="small"
            onClick={recordingState.isRecording ? stopRecording : startRecording}
            disabled={disabled || isProcessing}
            sx={{
                color: recordingState.isRecording ? '#f44336' :
                    isProcessing ? '#999' : '#7FB3C8',
                padding: '4px',
                '&:hover': {
                    backgroundColor: recordingState.isRecording ?
                        'rgba(244, 67, 54, 0.04)' :
                        'rgba(9, 65, 81, 0.04)'
                }
            }}
        >
            {recordingState.isRecording ?
                <MicOffIcon sx={{ fontSize: '18px' }} /> :
                <MicIcon sx={{ fontSize: '18px' }} />
            }
        </IconButton>
    );
});

const QuestionItem = memo(({
    question,
    index,
    answer,
    onAnswerChange,
    onDescriptionChange,
    onFindingsChange,
    onProposedSolutionsChange,
    onEditClick,
    onFileChange,
    onSaveEdit,
    isEditing,
    canDelete,
    onDelete
}: {
    question: Question;
    index: number;
    answer: Answer;
    onAnswerChange: (id: string, value: string) => void;
    onDescriptionChange: (id: string, value: string) => void;
    onFindingsChange: (id: string, value: string) => void;
    onProposedSolutionsChange: (id: string, value: string) => void;
    onEditClick: (id: string) => void;
    onFileChange: (id: string, files: File[], fileAttachmentIdsToRemove?: string[]) => void;
    onSaveEdit: (id: string) => void;
    isEditing: boolean;
    canDelete: boolean;
    onDelete: (id: string) => void;
}) => {
    const [isDownloading, setIsDownloading] = useState<{ [key: string]: boolean }>({});
    const [isGeneratingInsight, setIsGeneratingInsight] = useState(false);

    const handleFileAdd = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            const newFiles = Array.from(event.target.files);
            const maxFileSize = 12 * 1024 * 1024; // 12MB in bytes
            const validFiles: File[] = [];
            const invalidFiles: string[] = [];

            newFiles.forEach(file => {
                if (file.size > maxFileSize) {
                    invalidFiles.push(`${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB)`);
                } else {
                    validFiles.push(file);
                }
            });

            if (invalidFiles.length > 0) {
                console.warn('Files too large:', invalidFiles);
                alert(`Dosya boyutu 12MB'ı aşamaz:\n${invalidFiles.join('\n')}`);
            }

            if (validFiles.length > 0) {
                console.log('Adding new files:', validFiles.map(f => f.name));
                console.log('Current files:', answer.files?.map(f => f.name) || []);
                onFileChange(question.id, [...(answer.files || []), ...validFiles], answer.fileAttachmentIdsToRemove);
            }
        }
    };

    const handleFileRemove = (fileToRemove: File | { id: string; fileName: string }) => {
        if ('id' in fileToRemove) {
            // If it's an existing file (has ID) - add to removal list
            const updatedFilesToRemove = [...(answer.fileAttachmentIdsToRemove || []), fileToRemove.id];
            console.log('Removing existing file:', fileToRemove.fileName, 'ID:', fileToRemove.id);
            onFileChange(
                question.id,
                answer.files || [],
                updatedFilesToRemove
            );
        } else {
            // If it's a new file - remove from files array
            const updatedFiles = (answer.files || []).filter(file => file !== fileToRemove);
            console.log('Removing new file:', fileToRemove.name);
            onFileChange(question.id, updatedFiles, answer.fileAttachmentIdsToRemove);
        }
    };

    const handleFileDownload = async (fileId: string) => {
        try {
            setIsDownloading(prev => ({ ...prev, [fileId]: true }));
            const blob = await eventsApi.getFileAttachment(fileId);

            // Find the file attachment to get the correct filename
            const fileAttachment = answer.fileAttachments?.find(file => file.id === fileId);
            if (!fileAttachment) {
                throw new Error('File attachment not found');
            }

            // Create a URL for the blob
            const url = window.URL.createObjectURL(blob);

            // Create a temporary link element
            const link = document.createElement('a');
            link.href = url;
            link.download = fileAttachment.fileName; // Use the actual filename from fileAttachment

            // Append to body, click, and remove
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Clean up the URL
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading file:', error);
        } finally {
            setIsDownloading(prev => ({ ...prev, [fileId]: false }));
        }
    };

    return (
        <Paper sx={{ mb: 3, p: 2, backgroundColor: '#f9f9f9', borderRadius: 1, position: 'relative' }}>
            {(canDelete || answer.locked) && (
                <Box sx={{ position: 'absolute', right: 16, top: 12, display: 'flex', gap: 1, alignItems: 'center' }}>
                    {canDelete && (
                        <Tooltip title="Soruyu sil" placement="top">
                            <IconButton
                                size="small"
                                onClick={() => onDelete(question.id)}
                                sx={{
                                    color: '#b71c1c',
                                    '&:hover': { backgroundColor: 'rgba(183, 28, 28, 0.08)' }
                                }}
                            >
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}

                    {answer.locked && (
                        <Button
                            onClick={() => isEditing ? onSaveEdit(question.id) : onEditClick(question.id)}
                            sx={{
                                color: isEditing ? '#fff' : '#094151',
                                backgroundColor: isEditing ? '#094151' : 'transparent',
                                '&:hover': {
                                    backgroundColor: isEditing ? '#022631' : 'rgba(9, 65, 81, 0.08)',
                                    color: isEditing ? '#fff !important' : '#094151 !important',
                                }
                            }}
                        >
                            {isEditing ? 'Güncelle' : 'Düzenle'}
                        </Button>
                    )}
                </Box>
            )}

            <Typography sx={{ mb: 2, fontWeight: 500, pr: (canDelete || answer.locked) ? 8 : 0 }}>
                {index + 1}. {question.text}
            </Typography>

            <FormControl component="fieldset" sx={{ mb: 2 }} disabled={answer.locked && !isEditing}>
                <RadioGroup
                    row
                    value={answer?.answer || ''}
                    onChange={(e) => {
                        // Normal değişiklik durumunda değeri güncelle
                        onAnswerChange(question.id, e.target.value);
                    }}
                >
                    <FormControlLabel
                        value="evet"
                        control={<Radio />}
                        label="Evet"
                        onClick={(e) => {
                            // Eğer "evet" zaten seçiliyse, seçimi temizle
                            if (answer?.answer === 'evet') {
                                e.preventDefault();
                                onAnswerChange(question.id, '');
                            }
                        }}
                    />
                    <FormControlLabel
                        value="hayir"
                        control={<Radio />}
                        label="Hayır"
                        onClick={(e) => {
                            // Eğer "hayir" zaten seçiliyse, seçimi temizle
                            if (answer?.answer === 'hayir') {
                                e.preventDefault();
                                onAnswerChange(question.id, '');
                            }
                        }}
                    />
                </RadioGroup>
            </FormControl>

            <Grid sx={{ position: 'relative' }}>
                <TextField
                    fullWidth
                    size="small"
                    label="Bulgular/Düşünceler"
                    variant="outlined"
                    value={answer?.findings || ''}
                    onChange={(e) => onFindingsChange(question.id, e.target.value)}
                    sx={{
                        mb: 1,
                        display: 'flex',
                        backgroundColor: '#fff',
                        borderRadius: '6px !important',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '64px',
                        '& .MuiInputBase-root': { height: '64px' },
                        '& .MuiOutlinedInput-input': { height: '100%', padding: '0 14px' }
                    }}
                    multiline
                    rows={2}
                    disabled={answer.locked && !isEditing}
                />
                <Box sx={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 1
                }}>
                    <MicrophoneButton
                        onRecordingComplete={(audioBlob) => { }}
                        onTranscriptionComplete={(text) => {
                            onFindingsChange(question.id, text);
                        }}
                        disabled={answer.locked && !isEditing}
                    />
                </Box>
            </Grid>

            <Typography
                onClick={async () => {
                    // Soru locked ise ve düzenle modunda değilse tıklanmasın
                    if (answer.locked && !isEditing) return;
                    if (isGeneratingInsight) return;

                    setIsGeneratingInsight(true);
                    try {
                        const response = await eventsApi.generateInsight({
                            question: question.text,
                            tespit: answer.findings || ''
                        });
                        onProposedSolutionsChange(question.id, response.insight);
                    } catch (error) {
                        console.error('AI öneri alınırken hata:', error);
                    } finally {
                        setIsGeneratingInsight(false);
                    }
                }}
                sx={{
                    mb: 1,
                    color: (answer.locked && !isEditing) || isGeneratingInsight ? '#ccc' : '#196074',
                    cursor: (answer.locked && !isEditing) || isGeneratingInsight ? 'not-allowed' : 'pointer',
                    textDecoration: (answer.locked && !isEditing) ? 'none' : 'underline',
                    '&:hover': {
                        color: (answer.locked && !isEditing) || isGeneratingInsight ? '#ccc' : '#094151'
                    },
                    opacity: (answer.locked && !isEditing) || isGeneratingInsight ? 0.5 : 1,
                    userSelect: 'none',
                    '&:active': {
                        color: (answer.locked && !isEditing) || isGeneratingInsight ? '#ccc' : '#022631'
                    },
                    pointerEvents: (answer.locked && !isEditing) || isGeneratingInsight ? 'none' : 'auto'
                }}
            >
                {isGeneratingInsight ? 'Öneri oluşturuluyor...' : 'Otomatik Öneri Oluştur'}
            </Typography>

            <Grid sx={{ position: 'relative' }}>
                <TextField
                    fullWidth
                    size="small"
                    label="Öneriler"
                    variant="outlined"
                    value={answer?.proposedSolutions || ''}
                    onChange={(e) => onProposedSolutionsChange(question.id, e.target.value)}
                    sx={{
                        mb: 1,
                        display: 'flex',
                        backgroundColor: '#fff',
                        borderRadius: '6px !important',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '64px',
                        '& .MuiInputBase-root': { height: '64px' },
                        '& .MuiOutlinedInput-input': { height: '100%', padding: '0 14px' }
                    }}
                    multiline
                    rows={2}
                    disabled={answer.locked && !isEditing}
                />
                <Box sx={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 1
                }}>
                    <MicrophoneButton
                        onRecordingComplete={(audioBlob) => { }}
                        onTranscriptionComplete={(text) => {
                            onProposedSolutionsChange(question.id, text);
                        }}
                        disabled={answer.locked && !isEditing}
                    />
                </Box>
            </Grid>

            <Box sx={{
                border: '1px dashed #094151',
                borderRadius: 1,
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                backgroundColor: '#fff',
                opacity: answer.locked && !isEditing ? 0.7 : 1
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button
                        variant="outlined"
                        component="label"
                        disabled={answer.locked && !isEditing}
                        sx={{
                            width: '160px',
                            backgroundColor: '#196074',
                            color: "#fff",
                            '&:hover': {
                                backgroundColor: '#094151',
                                color: "#fff",
                                fontWeight: 600
                            }
                        }}
                    >
                        Dosya Seç
                        <input
                            type="file"
                            hidden
                            multiple
                            accept=".pdf,.jpg,.jpeg,.png,.PDF,.JPG,.JPEG,.PNG"
                            onChange={handleFileAdd}
                            disabled={answer.locked && !isEditing}
                        />
                    </Button>
                    <Typography variant="body2" color="textSecondary">
                        Desteklenen dosya formatları: PDF, JPEG, PNG (Max: 12MB)
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {answer.fileAttachments?.map((file) => (
                        !(answer.fileAttachmentIdsToRemove || []).includes(file.id) && (
                            <Box
                                key={file.id}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    backgroundColor: '#f0f0f0',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        backgroundColor: '#e0e0e0'
                                    }
                                }}
                                onClick={() => handleFileDownload(file.id)}
                            >
                                <Typography variant="body2">
                                    {file.fileName}
                                    {isDownloading[file.id] && ' (İndiriliyor...)'}
                                </Typography>
                                {(!answer.locked || isEditing) && (
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation(); // Prevent download when clicking remove
                                            handleFileRemove(file);
                                        }}
                                        sx={{ ml: 1 }}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>
                        )
                    ))}

                    {answer.files?.map((file, index) => (
                        <Box
                            key={index}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                backgroundColor: '#f0f0f0',
                                padding: '4px 8px',
                                borderRadius: '4px'
                            }}
                        >
                            <Typography variant="body2">{file.name}</Typography>
                            <IconButton
                                size="small"
                                onClick={() => handleFileRemove(file)}
                                sx={{ ml: 1 }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Paper>
    );
});

const AddCategoryBox = memo(({ onAddClick }: { onAddClick: () => void }) => (
    <Paper
        elevation={2}
        sx={{
            p: 2,
            height: '100px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            backgroundColor: '#f5f5f5',
            '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
            transition: 'background-color 0.3s'
        }}
        onClick={onAddClick}
    >
        <AddIcon sx={{ fontSize: 32, color: '#666' }} />
        <Typography sx={{ mt: 1, color: '#666' }}>Yeni Kategori Ekle</Typography>
    </Paper>
));

const AddQuestionBox = memo(({ onAddClick }: { onAddClick: () => void }) => (
    <Paper
        elevation={2}
        sx={{
            p: 2,
            mt: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            backgroundColor: '#f5f5f5',
            '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
            transition: 'background-color 0.3s'
        }}
        onClick={onAddClick}
    >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddIcon sx={{ fontSize: 24, color: '#666' }} />
            <Typography sx={{ color: '#666' }}>Yeni Soru Ekle</Typography>
        </Box>
    </Paper>
));

const ChecklistModal: React.FC<ChecklistModalProps> = ({ open, onClose, pinId }) => {
    const [categories, setCategories] = useState<QuestionCategory[]>([]);
    const [questions, setQuestions] = useState<Question[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const [answers, setAnswers] = useState<FormAnswers>({});
    const [loading, setLoading] = useState(true);
    const [riskTablesData, setRiskTablesData] = useState<{
        [key: string]: {
            table1: RiskTableData;
            table2: RiskTableData;
        }
    }>({});
    const [categoryQuestionCounts, setCategoryQuestionCounts] = useState<{ [key: string]: number }>({});
    const [userId, setUserId] = useState<string>('');
    const [newCategoryDialogOpen, setNewCategoryDialogOpen] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [newQuestionDialogOpen, setNewQuestionDialogOpen] = useState(false);
    const [newQuestionText, setNewQuestionText] = useState('');
    const [selectedCategoryName, setSelectedCategoryName] = useState<string>('');
    const [categoryAnsweredCounts, setCategoryAnsweredCounts] = useState<{ [key: string]: number }>({});
    const [editingQuestionId, setEditingQuestionId] = useState<string | null>(null);
    const [riskAnswers, setRiskAnswers] = useState<{
        [key: string]: {
            beforeMeasures?: CategoryRiskAnswerResponse;
            afterMeasures?: CategoryRiskAnswerResponse;
        }
    }>({});
    const [editingRiskTable, setEditingRiskTable] = useState<{
        categoryId: string | null;
        isAfterMeasures: boolean;
    }>({ categoryId: null, isAfterMeasures: false });

    // Yeni state'ler
    const [requiredQuestionDialogOpen, setRequiredQuestionDialogOpen] = useState(false);
    const [newlyCreatedCategoryId, setNewlyCreatedCategoryId] = useState<string>('');
    const [requiredQuestionText, setRequiredQuestionText] = useState('');

    // Dosya yönetimi için state'ler
    const [filesToRemove, setFilesToRemove] = useState<{ [questionId: string]: string[] }>({});

    // Category delete (admin/superadmin only)
    const [canDeleteCategories, setCanDeleteCategories] = useState(false);
    const [deleteCategoryDialogOpen, setDeleteCategoryDialogOpen] = useState(false);
    const [categoryToDelete, setCategoryToDelete] = useState<{ id: string; name: string } | null>(null);

    // Question delete (admin/superadmin only)
    const [deleteQuestionDialogOpen, setDeleteQuestionDialogOpen] = useState(false);
    const [questionToDelete, setQuestionToDelete] = useState<{ id: string; text: string } | null>(null);

    const DEFAULT_QUESTION_TYPE_ID = "d5d3dd45-0a2e-457d-933b-c66a3853469f";

    const sortQuestionsStable = (qs: any[]) => {
        return [...qs].sort((a, b) => {
            const ai = typeof a?.index === 'number' ? a.index : Number.MAX_SAFE_INTEGER;
            const bi = typeof b?.index === 'number' ? b.index : Number.MAX_SAFE_INTEGER;
            if (ai !== bi) return ai - bi;

            const at = (a?.text ?? '').toString();
            const bt = (b?.text ?? '').toString();
            return at.localeCompare(bt, 'tr-TR', { sensitivity: 'base' });
        });
    };

    const storeUserId = useSelector((state: any) => state.auth.id);

    useEffect(() => {
        if (!storeUserId) {
            console.error('User ID not found');
            return;
        }
        setUserId(storeUserId);
    }, [storeUserId]);

    useEffect(() => {
        if (!open) return;

        const checkDeleteAccess = async () => {
            try {
                const user = await eventsApi.getUser();
                const superadminClaimId = '00000000-0000-0000-0000-000000000001';
                const adminClaimId = '00000000-0000-0000-0000-000000000002';
                setCanDeleteCategories(
                    user.operationClaimIds?.includes(superadminClaimId) ||
                    user.operationClaimIds?.includes(adminClaimId) ||
                    false
                );
            } catch (e) {
                console.error('Error checking delete access:', e);
                setCanDeleteCategories(false);
            }
        };

        checkDeleteAccess();
    }, [open]);

    // Modal açıldığında risk verilerini yükle
    useEffect(() => {
        const loadRiskData = async () => {
            if (open && pinId) {
                try {
                    const riskAnswersData = await eventsApi.getCategoryRiskAnswers(pinId);
                    const riskAnswersMap: {
                        [key: string]: {
                            beforeMeasures?: any;
                            afterMeasures?: any;
                        }
                    } = {};

                    if (Array.isArray(riskAnswersData) && riskAnswersData.length > 0) {
                        riskAnswersData.forEach((ra: any) => {
                            const catId = ra.questionCategoryId || ra.categoryName;
                            if (!catId) {
                                return;
                            }
                            if (!riskAnswersMap[catId]) {
                                riskAnswersMap[catId] = {};
                            }
                            // isAfterMeasures boolean olarak gelmeli
                            const isAfter = ra.isAfterMeasures === true || ra.isAfterMeasures === 'true' || ra.isAfterMeasures === 1;
                            if (isAfter) {
                                riskAnswersMap[catId].afterMeasures = ra;
                            } else {
                                riskAnswersMap[catId].beforeMeasures = ra;
                            }
                        });
                    }
                    setRiskAnswers(riskAnswersMap);

                    // Risk tablosu verilerini riskAnswers'dan yükle
                    // Mevcut riskTablesData'yı koru
                    setRiskTablesData(prev => {
                        const riskTablesMap: {
                            [key: string]: {
                                table1: RiskTableData;
                                table2: RiskTableData;
                            }
                        } = { ...prev };

                        // Tüm kategoriler için risk verilerini yükle
                        Object.keys(riskAnswersMap).forEach(catId => {
                            const before = riskAnswersMap[catId]?.beforeMeasures;
                            const after = riskAnswersMap[catId]?.afterMeasures;

                            riskTablesMap[catId] = {
                                table1: before ? {
                                    probabilityLevel: before.probabilityLevel.toString(),
                                    impactLevel: before.impactLevel.toString(),
                                    threatLevel: before.threatLevel.toString(),
                                    measureCoefficient: before.appliedMeasureCoefficient.toString(),
                                    residualRiskLevel: before.residualRiskLevel.toString()
                                } : (prev[catId]?.table1 || getEmptyRiskTableData()),
                                table2: after ? {
                                    probabilityLevel: after.probabilityLevel.toString(),
                                    impactLevel: after.impactLevel.toString(),
                                    threatLevel: after.threatLevel.toString(),
                                    measureCoefficient: after.appliedMeasureCoefficient.toString(),
                                    residualRiskLevel: after.residualRiskLevel.toString()
                                } : (prev[catId]?.table2 || getEmptyRiskTableData())
                            };
                        });

                        return riskTablesMap;
                    });
                } catch (error) {
                    console.error('Error loading risk data:', error);
                }
            }
        };
        loadRiskData();
    }, [open, pinId]);

    useEffect(() => {
        const fetchData = async () => {
            if (open) {
                console.log('ChecklistModal opened, fetching data...');
                try {
                    const response = await eventsApi.getPinQuestionsAndAnswers(pinId);

                    // Kategorileri ID'ye göre grupla ve tekrar edenleri filtrele
                    const categoryMap = response.questions.reduce((acc, question) => {
                        const category = question.questionCategory;
                        if (!acc.has(category.id)) {
                            acc.set(category.id, category);
                        }
                        return acc;
                    }, new Map<string, QuestionCategory>());

                    // Map'ten array'e çevir, filtrele ve sırala
                    const uniqueCategories = Array.from(categoryMap.values())
                        .filter(category => category.name !== "SONUÇ VE DEĞERLENDİRME")
                        .sort((a, b) => {
                            // If both indices are null, maintain original order
                            if (a.index === null && b.index === null) return 0;
                            // If only a's index is null, put it at the end
                            if (a.index === null) return 1;
                            // If only b's index is null, put it at the end
                            if (b.index === null) return -1;
                            // If both have indices, sort normally
                            return a.index - b.index;
                        });

                    setCategories(uniqueCategories);

                    // Kategori başına soru sayılarını hesapla
                    const counts = response.questions.reduce((acc, question) => {
                        const categoryId = question.questionCategory.id;
                        acc[categoryId] = (acc[categoryId] || 0) + 1;
                        return acc;
                    }, {} as { [key: string]: number });
                    setCategoryQuestionCounts(counts);

                    // Cevapları formatla
                    const formattedAnswers = response.questions.reduce((acc, question) => {
                        if (question.answers && question.answers.length > 0) {
                            const answer = question.answers[0]; // İlk cevabı al
                            acc[question.id] = {
                                answer: answer.yesNo ? 'evet' : 'hayir',
                                findings: answer.findings,
                                description: answer.description,
                                proposedSolutions: answer.proposedSolutions,
                                locked: true,
                                questionAnswerId: answer.id,
                                fileAttachments: answer.fileAttachments
                            };
                        }
                        return acc;
                    }, {} as FormAnswers);
                    setAnswers(formattedAnswers);

                    // Kategori başına cevaplanan soru sayılarını hesapla
                    const answeredCounts = response.questions.reduce((acc, question) => {
                        if (question.answers && question.answers.length > 0) {
                            const categoryId = question.questionCategory.id;
                            acc[categoryId] = (acc[categoryId] || 0) + 1;
                        }
                        return acc;
                    }, {} as { [key: string]: number });
                    setCategoryAnsweredCounts(answeredCounts);

                    console.log('Data fetched successfully');
                } catch (error) {
                    console.error('Error fetching data:', error);
                } finally {
                    setLoading(false);
                }
            } else {
                // Modal kapandığında state'leri temizle
                console.log('ChecklistModal closed, clearing states...');
                setSelectedCategory(null);
                setQuestions([]);
                setSelectedCategoryName('');
                setEditingQuestionId(null);
                setFilesToRemove({});
                setAnswers({});
                setLoading(true);
            }
        };

        fetchData();
    }, [open, pinId]);

    const handleConfirmDeleteCategory = async () => {
        if (!categoryToDelete) return;

        try {
            await eventsApi.deleteQuestionCategory(categoryToDelete.id);

            // Optimistic UI update; backend enforces Admin/Superadmin
            setCategories((prev) => prev.filter((c) => c.id !== categoryToDelete.id));
            setCategoryQuestionCounts((prev) => {
                const copy = { ...prev };
                delete copy[categoryToDelete.id];
                return copy;
            });
            setCategoryAnsweredCounts((prev) => {
                const copy = { ...prev };
                delete copy[categoryToDelete.id];
                return copy;
            });
        } catch (e) {
            console.error('Kategori silme hatası:', e);
            alert('Kategori silinemedi. Yetkiniz olmayabilir veya kategori silinemez durumda olabilir.');
        } finally {
            setDeleteCategoryDialogOpen(false);
            setCategoryToDelete(null);
        }
    };

    const handleConfirmDeleteQuestion = async () => {
        if (!questionToDelete || !selectedCategory) return;

        try {
            await eventsApi.deleteQuestion(questionToDelete.id);

            const wasAnswered = !!answers[questionToDelete.id]?.answer;

            setQuestions((prev) => prev.filter((q) => q.id !== questionToDelete.id));
            setAnswers((prev) => {
                const copy = { ...prev };
                delete copy[questionToDelete.id];
                return copy;
            });

            setCategoryQuestionCounts((prev) => ({
                ...prev,
                [selectedCategory]: Math.max(0, (prev[selectedCategory] || 0) - 1),
            }));

            if (wasAnswered) {
                setCategoryAnsweredCounts((prev) => ({
                    ...prev,
                    [selectedCategory]: Math.max(0, (prev[selectedCategory] || 0) - 1),
                }));
            }
        } catch (e) {
            console.error('Soru silme hatası:', e);
            alert('Soru silinemedi. Yetkiniz olmayabilir veya soru silinemez durumda olabilir.');
        } finally {
            setDeleteQuestionDialogOpen(false);
            setQuestionToDelete(null);
        }
    };

    const handleCategoryClick = async (categoryId: string) => {
        if (!categoryId) return;

        setLoading(true);
        try {
            const response = await eventsApi.getPinQuestionsAndAnswers(pinId);
            const categoryQuestions = response.questions.filter(
                q => q.questionCategory.id === categoryId
            );
            setQuestions(sortQuestionsStable(categoryQuestions) as unknown as Question[]);
            setSelectedCategory(categoryId);

            const category = categories.find(c => c.id === categoryId);
            if (category) {
                setSelectedCategoryName(category.name);
            }

            // Risk cevaplarını yükle (mevcut state'i koruyarak güncelle)
            const riskAnswersData = await eventsApi.getCategoryRiskAnswers(pinId);
            const riskAnswersMap: {
                [key: string]: {
                    beforeMeasures?: any;
                    afterMeasures?: any;
                }
            } = { ...riskAnswers }; // Mevcut state'i koru

            if (Array.isArray(riskAnswersData) && riskAnswersData.length > 0) {
                riskAnswersData.forEach((ra: any) => {
                    const catId = ra.questionCategoryId || ra.categoryName;
                    if (!catId) {
                        return;
                    }
                    if (!riskAnswersMap[catId]) {
                        riskAnswersMap[catId] = {};
                    }
                    // isAfterMeasures boolean olarak gelmeli
                    const isAfter = ra.isAfterMeasures === true || ra.isAfterMeasures === 'true' || ra.isAfterMeasures === 1;
                    if (isAfter) {
                        riskAnswersMap[catId].afterMeasures = ra;
                    } else {
                        riskAnswersMap[catId].beforeMeasures = ra;
                    }
                });
            }
            setRiskAnswers(riskAnswersMap);

            // Risk tablosu verilerini riskAnswers'dan yükle (mevcut state'i koruyarak güncelle)
            setRiskTablesData(prev => {
                const riskTablesMap: {
                    [key: string]: {
                        table1: RiskTableData;
                        table2: RiskTableData;
                    }
                } = { ...prev }; // Mevcut state'i koru

                Object.keys(riskAnswersMap).forEach(catId => {
                    const before = riskAnswersMap[catId]?.beforeMeasures;
                    const after = riskAnswersMap[catId]?.afterMeasures;

                    riskTablesMap[catId] = {
                        table1: before ? {
                            probabilityLevel: before.probabilityLevel.toString(),
                            impactLevel: before.impactLevel.toString(),
                            threatLevel: before.threatLevel.toString(),
                            measureCoefficient: before.appliedMeasureCoefficient.toString(),
                            residualRiskLevel: before.residualRiskLevel.toString()
                        } : (prev[catId]?.table1 || getEmptyRiskTableData()),
                        table2: after ? {
                            probabilityLevel: after.probabilityLevel.toString(),
                            impactLevel: after.impactLevel.toString(),
                            threatLevel: after.threatLevel.toString(),
                            measureCoefficient: after.appliedMeasureCoefficient.toString(),
                            residualRiskLevel: after.residualRiskLevel.toString()
                        } : (prev[catId]?.table2 || getEmptyRiskTableData())
                    };
                });

                return riskTablesMap;
            });
        } catch (error) {
            console.error('Error fetching questions:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleBackClick = async () => {
        try {
            console.log('Going back to category list, clearing states...');

            const response = await eventsApi.getPinQuestionsAndAnswers(pinId);

            // Cevapları formatla
            const formattedAnswers = response.questions.reduce((acc, question) => {
                if (question.answers && question.answers.length > 0) {
                    const answer = question.answers[0];
                    acc[question.id] = {
                        answer: answer.yesNo ? 'evet' : 'hayir',
                        findings: answer.findings,
                        description: answer.description,
                        proposedSolutions: answer.proposedSolutions,
                        locked: true,
                        questionAnswerId: answer.id,
                        fileAttachments: answer.fileAttachments
                    };
                }
                return acc;
            }, {} as FormAnswers);

            // Kategori başına cevaplanan soru sayılarını hesapla
            const answeredCounts = response.questions.reduce((acc, question) => {
                if (question.answers && question.answers.length > 0) {
                    const categoryId = question.questionCategory.id;
                    acc[categoryId] = (acc[categoryId] || 0) + 1;
                }
                return acc;
            }, {} as { [key: string]: number });

            // State'leri temizle
            setCategoryAnsweredCounts(answeredCounts);
            setSelectedCategory(null);
            setQuestions([]);
            setSelectedCategoryName('');
            setEditingQuestionId(null);
            setFilesToRemove({});
            setAnswers(formattedAnswers);

            console.log('States cleared successfully');
        } catch (error) {
            console.error('Error updating answered counts:', error);
        }
    };

    const handleAnswerChange = (questionId: string, value: string) => {
        setAnswers(prev => {
            const updatedAnswers = {
                ...prev,
                [questionId]: {
                    ...prev[questionId],
                    answer: value
                }
            };
            return updatedAnswers;
        });
    };

    const handleDescriptionChange = (questionId: string, value: string) => {
        setAnswers(prev => ({
            ...prev,
            [questionId]: { ...prev[questionId], description: value }
        }));
    };

    const handleFindingsChange = (questionId: string, value: string) => {
        setAnswers(prev => ({
            ...prev,
            [questionId]: { ...prev[questionId], findings: value }
        }));
    };

    const handleProposedSolutionsChange = (questionId: string, value: string) => {
        setAnswers(prev => ({
            ...prev,
            [questionId]: { ...prev[questionId], proposedSolutions: value }
        }));
    };

    const handleFileChange = (questionId: string, files: File[], fileAttachmentIdsToRemove?: string[]) => {
        console.log('File change for question:', questionId, {
            newFiles: files.map(f => f.name),
            filesToRemove: fileAttachmentIdsToRemove
        });

        setAnswers(prev => ({
            ...prev,
            [questionId]: { ...prev[questionId], files, fileAttachmentIdsToRemove }
        }));

        // filesToRemove state'ini güncelle
        if (fileAttachmentIdsToRemove) {
            setFilesToRemove(prev => ({
                ...prev,
                [questionId]: fileAttachmentIdsToRemove
            }));
        }
    };

    const getEmptyRiskTableData = (): RiskTableData => ({
        probabilityLevel: '',
        impactLevel: '',
        threatLevel: '',
        measureCoefficient: '',
        residualRiskLevel: '',
    });

    const handleRiskTableChange = (questionId: string, tableIndex: number, field: keyof RiskTableData, value: string) => {
        setRiskTablesData(prev => ({
            ...prev,
            [questionId]: {
                ...prev[questionId] || {},
                ...(tableIndex === 1 ? {
                    table1: {
                        ...((prev[questionId] || {})?.table1 || getEmptyRiskTableData()),
                        [field]: value
                    },
                    table2: (prev[questionId] || {})?.table2 || getEmptyRiskTableData()
                } : {
                    table1: (prev[questionId] || {})?.table1 || getEmptyRiskTableData(),
                    table2: {
                        ...((prev[questionId] || {})?.table2 || getEmptyRiskTableData()),
                        [field]: value
                    }
                })
            }
        }));
    };

    const handleRiskTableSave = async (categoryId: string, isAfterMeasures: boolean) => {
        const tableData = riskTablesData[categoryId]?.[isAfterMeasures ? 'table2' : 'table1'];
        if (!tableData) {
            console.warn('No table data found for category:', categoryId, 'isAfterMeasures:', isAfterMeasures);
            return;
        }

        // En az bir alan dolu olmalı
        const hasData = (tableData.probabilityLevel && tableData.probabilityLevel.trim() !== '') ||
            (tableData.impactLevel && tableData.impactLevel.trim() !== '') ||
            (tableData.measureCoefficient && tableData.measureCoefficient.trim() !== '');

        if (!hasData) {
            console.warn('No data to save for category:', categoryId, 'isAfterMeasures:', isAfterMeasures);
            return;
        }

        // Tehdit seviyesi hesaplama
        const probabilityLevel = parseFloat(tableData.probabilityLevel) || 0;
        const impactLevel = parseFloat(tableData.impactLevel) || 0;
        const threatLevel = probabilityLevel * impactLevel;

        // Kalıntı risk seviyesi hesaplama
        const appliedMeasureCoefficient = parseFloat(tableData.measureCoefficient) || 0;
        const residualRiskLevel = threatLevel * appliedMeasureCoefficient;

        const data: CategoryRiskAnswer = {
            questionCategoryId: categoryId,
            pinId,
            userId,
            probabilityLevel,
            impactLevel,
            threatLevel,
            appliedMeasureCoefficient,
            residualRiskLevel,
            isAfterMeasures: Boolean(isAfterMeasures)
        };

        try {
            const response = await eventsApi.saveCategoryRiskAnswers(data);
            setRiskAnswers(prev => ({
                ...prev,
                [categoryId]: {
                    ...prev[categoryId],
                    [isAfterMeasures ? 'afterMeasures' : 'beforeMeasures']: response
                }
            }));

            // Risk tablosu verilerini de güncelle
            setRiskTablesData(prev => ({
                ...prev,
                [categoryId]: {
                    ...prev[categoryId] || { table1: getEmptyRiskTableData(), table2: getEmptyRiskTableData() },
                    [isAfterMeasures ? 'table2' : 'table1']: {
                        probabilityLevel: probabilityLevel.toString(),
                        impactLevel: impactLevel.toString(),
                        threatLevel: threatLevel.toString(),
                        measureCoefficient: appliedMeasureCoefficient.toString(),
                        residualRiskLevel: residualRiskLevel.toString()
                    }
                }
            }));
        } catch (error) {
            console.error('Risk tablosu kaydedilirken hata:', error);
        }
    };

    const handleRiskTableEdit = (categoryId: string, isAfterMeasures: boolean) => {
        setEditingRiskTable({ categoryId, isAfterMeasures });
    };

    const handleRiskTableUpdate = async (categoryId: string, isAfterMeasures: boolean) => {
        const tableData = riskTablesData[categoryId]?.[isAfterMeasures ? 'table2' : 'table1'];
        const existingAnswer = isAfterMeasures ?
            riskAnswers[categoryId]?.afterMeasures :
            riskAnswers[categoryId]?.beforeMeasures;

        if (!existingAnswer) {
            console.error('Existing answer not found for update');
            return;
        }

        // Eğer tableData yoksa, mevcut değerleri kullan
        const dataToUse = tableData || (existingAnswer ? {
            probabilityLevel: existingAnswer.probabilityLevel.toString(),
            impactLevel: existingAnswer.impactLevel.toString(),
            threatLevel: existingAnswer.threatLevel.toString(),
            measureCoefficient: existingAnswer.appliedMeasureCoefficient.toString(),
            residualRiskLevel: existingAnswer.residualRiskLevel.toString()
        } : getEmptyRiskTableData());

        // Tehdit seviyesi hesaplama
        const probabilityLevel = parseFloat(dataToUse.probabilityLevel) || 0;
        const impactLevel = parseFloat(dataToUse.impactLevel) || 0;
        const threatLevel = probabilityLevel * impactLevel;

        // Kalıntı risk seviyesi hesaplama
        const appliedMeasureCoefficient = parseFloat(dataToUse.measureCoefficient) || 0;
        const residualRiskLevel = threatLevel * appliedMeasureCoefficient;

        const data: CategoryRiskAnswerUpdate = {
            id: existingAnswer.id,
            questionCategoryId: categoryId,
            pinId,
            userId,
            probabilityLevel,
            impactLevel,
            threatLevel,
            appliedMeasureCoefficient,
            residualRiskLevel,
            isAfterMeasures: Boolean(isAfterMeasures)
        };

        try {
            const response = await eventsApi.updateCategoryRiskAnswers(data);
            // Güncellenmiş veriyi state'e kaydet
            setRiskAnswers(prev => ({
                ...prev,
                [categoryId]: {
                    ...prev[categoryId],
                    [isAfterMeasures ? 'afterMeasures' : 'beforeMeasures']: response
                }
            }));

            // Risk tablosu verilerini de güncelle
            setRiskTablesData(prev => ({
                ...prev,
                [categoryId]: {
                    ...prev[categoryId] || { table1: getEmptyRiskTableData(), table2: getEmptyRiskTableData() },
                    [isAfterMeasures ? 'table2' : 'table1']: {
                        probabilityLevel: probabilityLevel.toString(),
                        impactLevel: impactLevel.toString(),
                        threatLevel: threatLevel.toString(),
                        measureCoefficient: appliedMeasureCoefficient.toString(),
                        residualRiskLevel: residualRiskLevel.toString()
                    }
                }
            }));

            // Düzenleme modunu kapat (sadece manuel düzenleme butonundan çağrıldığında)
            if (editingRiskTable.categoryId === categoryId && editingRiskTable.isAfterMeasures === isAfterMeasures) {
                setEditingRiskTable({ categoryId: null, isAfterMeasures: false });
            }
        } catch (error) {
            console.error('Risk tablosu güncellenirken hata:', error);
        }
    };

    const handleSave = async () => {
        if (!userId) {
            console.error('User ID is required');
            return;
        }

        try {
            const unlockedAnswers = Object.entries(answers).filter(([_, answer]) => !answer.locked);
            console.log('Saving unlocked answers:', unlockedAnswers.length);

            const savePromises = unlockedAnswers.map(([questionId, answer]) => {
                console.log(`Saving answer for question ${questionId}:`, {
                    hasFiles: !!answer.files?.length,
                    fileCount: answer.files?.length || 0,
                    filesToRemove: answer.fileAttachmentIdsToRemove?.length || 0,
                    fileNames: answer.files?.map(f => f.name) || [],
                    filesToRemoveIds: answer.fileAttachmentIdsToRemove || []
                });

                const questionAnswer: Omit<QuestionAnswer, 'id'> = {
                    questionId: questionId,
                    userId: userId,
                    yesNo: answer.answer === 'evet',
                    findings: answer.findings || '',
                    pinId: pinId,
                    proposedSolutions: answer.proposedSolutions || '',
                    description: answer.description || '',
                    files: answer.files,
                    fileAttachmentIdsToRemove: answer.fileAttachmentIdsToRemove
                };

                setAnswers(prev => ({
                    ...prev,
                    [questionId]: { ...prev[questionId], locked: true }
                }));

                return eventsApi.saveQuestionAnswers(questionAnswer);
            });

            await Promise.all(savePromises);
            console.log('All answers saved successfully');

            // Risk tablolarını kaydet veya güncelle
            if (selectedCategory) {
                const table1Data = riskTablesData[selectedCategory]?.table1;
                const table2Data = riskTablesData[selectedCategory]?.table2;
                const existingBeforeMeasures = riskAnswers[selectedCategory]?.beforeMeasures;
                const existingAfterMeasures = riskAnswers[selectedCategory]?.afterMeasures;

                // Öncesi tablo - eğer veri varsa (en az bir alan dolu olmalı)
                if (table1Data) {
                    const hasData = table1Data.probabilityLevel !== '' ||
                        table1Data.impactLevel !== '' ||
                        table1Data.measureCoefficient !== '';

                    if (hasData) {
                        if (existingBeforeMeasures) {
                            // Güncelleme - direkt güncelle
                            await handleRiskTableUpdate(selectedCategory, false);
                        } else {
                            // Yeni kayıt
                            await handleRiskTableSave(selectedCategory, false);
                        }
                    }
                }

                // Sonrası tablo - eğer veri varsa (en az bir alan dolu olmalı)
                if (table2Data) {
                    const hasData = table2Data.probabilityLevel !== '' ||
                        table2Data.impactLevel !== '' ||
                        table2Data.measureCoefficient !== '';

                    if (hasData) {
                        if (existingAfterMeasures) {
                            // Güncelleme - direkt güncelle
                            await handleRiskTableUpdate(selectedCategory, true);
                        } else {
                            // Yeni kayıt
                            await handleRiskTableSave(selectedCategory, true);
                        }
                    }
                }
            }

            // Risk cevaplarını yeniden yükle
            try {
                const riskAnswersData = await eventsApi.getCategoryRiskAnswers(pinId);
                const riskAnswersMap: {
                    [key: string]: {
                        beforeMeasures?: any;
                        afterMeasures?: any;
                    }
                } = {};

                if (Array.isArray(riskAnswersData) && riskAnswersData.length > 0) {
                    riskAnswersData.forEach((ra: any) => {
                        const catId = ra.questionCategoryId || ra.categoryName;
                        if (!catId) {
                            return;
                        }
                        if (!riskAnswersMap[catId]) {
                            riskAnswersMap[catId] = {};
                        }
                        // isAfterMeasures boolean olarak gelmeli
                        const isAfter = ra.isAfterMeasures === true || ra.isAfterMeasures === 'true' || ra.isAfterMeasures === 1;
                        if (isAfter) {
                            riskAnswersMap[catId].afterMeasures = ra;
                        } else {
                            riskAnswersMap[catId].beforeMeasures = ra;
                        }
                    });
                }
                setRiskAnswers(riskAnswersMap);

                // Risk tablosu verilerini riskAnswers'dan yükle (mevcut state'i koruyarak güncelle)
                setRiskTablesData(prev => {
                    const riskTablesMap: {
                        [key: string]: {
                            table1: RiskTableData;
                            table2: RiskTableData;
                        }
                    } = { ...prev }; // Mevcut state'i koru

                    Object.keys(riskAnswersMap).forEach(catId => {
                        const before = riskAnswersMap[catId]?.beforeMeasures;
                        const after = riskAnswersMap[catId]?.afterMeasures;

                        riskTablesMap[catId] = {
                            table1: before ? {
                                probabilityLevel: before.probabilityLevel.toString(),
                                impactLevel: before.impactLevel.toString(),
                                threatLevel: before.threatLevel.toString(),
                                measureCoefficient: before.appliedMeasureCoefficient.toString(),
                                residualRiskLevel: before.residualRiskLevel.toString()
                            } : (prev[catId]?.table1 || getEmptyRiskTableData()),
                            table2: after ? {
                                probabilityLevel: after.probabilityLevel.toString(),
                                impactLevel: after.impactLevel.toString(),
                                threatLevel: after.threatLevel.toString(),
                                measureCoefficient: after.appliedMeasureCoefficient.toString(),
                                residualRiskLevel: after.residualRiskLevel.toString()
                            } : (prev[catId]?.table2 || getEmptyRiskTableData())
                        };
                    });

                    return riskTablesMap;
                });

            } catch (error) {
                console.error('Error reloading risk answers:', error);
            }

            // Yeni verileri yükle
            const response = await eventsApi.getPinQuestionsAndAnswers(pinId);

            // Cevapları formatla
            const formattedAnswers = response.questions.reduce((acc: FormAnswers, question) => {
                if (question.answers && question.answers.length > 0) {
                    const answer = question.answers[0];
                    acc[question.id] = {
                        answer: answer.yesNo ? 'evet' : 'hayir',
                        findings: answer.findings,
                        description: answer.description,
                        proposedSolutions: answer.proposedSolutions,
                        locked: true,
                        questionAnswerId: answer.id,
                        fileAttachments: answer.fileAttachments
                    };
                }
                return acc;
            }, {} as FormAnswers);
            setAnswers(formattedAnswers);

            // Kategori başına cevaplanan soru sayılarını güncelle
            const answeredCounts = response.questions.reduce((acc: { [key: string]: number }, question) => {
                if (question.answers && question.answers.length > 0) {
                    const categoryId = question.questionCategory.id;
                    acc[categoryId] = (acc[categoryId] || 0) + 1;
                }
                return acc;
            }, {});
            setCategoryAnsweredCounts(answeredCounts);

            // Kategorileri güncelle ve ana sayfaya dön
            const categoryMap = response.questions.reduce((acc, question) => {
                const category = question.questionCategory;
                if (!acc.has(category.id)) {
                    acc.set(category.id, category);
                }
                return acc;
            }, new Map<string, QuestionCategory>());

            const uniqueCategories = Array.from(categoryMap.values())
                .filter(category => category.name !== "SONUÇ VE DEĞERLENDİRME")
                .sort((a, b) => {
                    if (a.index === null && b.index === null) return 0;
                    if (a.index === null) return 1;
                    if (b.index === null) return -1;
                    return a.index - b.index;
                });

            setCategories(uniqueCategories);
            setSelectedCategory(null);
            setQuestions([]);
            setSelectedCategoryName('');

        } catch (error) {
            console.error('Cevaplar kaydedilirken hata oluştu:', error);
            console.error('Error details:', {
                message: error instanceof Error ? error.message : 'Unknown error',
                stack: error instanceof Error ? error.stack : undefined
            });
        }
    };

    const handleEditClick = (questionId: string) => {
        setEditingQuestionId(questionId);
    };

    const handleSaveEdit = async (questionId: string) => {
        try {
            const answer = answers[questionId];
            if (!answer.questionAnswerId) {
                console.error('Question answer ID not found');
                return;
            }

            console.log(`Updating answer for question ${questionId}:`, {
                hasFiles: !!answer.files?.length,
                fileCount: answer.files?.length || 0,
                filesToRemove: answer.fileAttachmentIdsToRemove?.length || 0,
                fileNames: answer.files?.map(f => f.name) || [],
                filesToRemoveIds: answer.fileAttachmentIdsToRemove || []
            });

            const formData = new FormData();
            formData.append('Id', answer.questionAnswerId);
            formData.append('QuestionId', questionId);
            formData.append('UserId', userId);
            formData.append('YesNo', answer.answer === 'evet' ? 'true' : 'false');
            formData.append('Findings', answer.findings || '');
            formData.append('PinId', pinId);
            formData.append('ProposedSolutions', answer.proposedSolutions || '');
            formData.append('Description', answer.description || '');

            // Add new files
            if (answer.files) {
                answer.files.forEach(file => {
                    formData.append('Files', file);
                });
            }

            // Add files to remove
            if (answer.fileAttachmentIdsToRemove) {
                answer.fileAttachmentIdsToRemove.forEach(fileId => {
                    formData.append('FileAttachmentIdsToRemove', fileId);
                });
            }

            await eventsApi.updateQuestionAnswer(formData);
            console.log(`Answer updated successfully for question ${questionId}`);

            // Yeni verileri yükle
            const response = await eventsApi.getPinQuestionsAndAnswers(pinId);

            if (selectedCategory) {
                const categoryQuestions = response.questions.filter(
                    q => q.questionCategory.id === selectedCategory
                );
                setQuestions(sortQuestionsStable(categoryQuestions) as unknown as Question[]);
            }

            // Cevapları formatla
            const formattedAnswers = response.questions.reduce((acc: FormAnswers, question) => {
                if (question.answers && question.answers.length > 0) {
                    const answer = question.answers[0];
                    acc[question.id] = {
                        answer: answer.yesNo ? 'evet' : 'hayir',
                        findings: answer.findings,
                        description: answer.description,
                        proposedSolutions: answer.proposedSolutions,
                        locked: true,
                        questionAnswerId: answer.id,
                        fileAttachments: answer.fileAttachments
                    };
                }
                return acc;
            }, {} as FormAnswers);
            setAnswers(formattedAnswers);

            setEditingQuestionId(null);

        } catch (error) {
            console.error('Soru güncellenirken hata oluştu:', error);
            console.error('Error details:', {
                message: error instanceof Error ? error.message : 'Unknown error',
                stack: error instanceof Error ? error.stack : undefined,
                questionId: questionId
            });
        }
    };

    const getInfoText = () => {
        if (!selectedCategory) {
            return `Risk Analiz Formu ${categories.length} bölümden oluşmaktadır.`;
        }
        return `Bu bölümde ${questions.length} soru bulunmaktadır.`;
    };

    const handleAddCategory = async () => {
        if (!newCategoryName.trim() || !userId) {
            return;
        }

        try {
            const categoryData: QuestionCategoryCreate = {
                name: newCategoryName.trim(),
                userId: userId,
                questionTypeId: DEFAULT_QUESTION_TYPE_ID,
                pinId: pinId
            };

            const newCategory = await eventsApi.saveQuestionCategory(categoryData) as CategoryResponse;
            setNewlyCreatedCategoryId(newCategory.id);

            // Dialog'u kapat ve zorunlu soru modalını aç
            setNewCategoryName('');
            setNewCategoryDialogOpen(false);
            setRequiredQuestionDialogOpen(true);

        } catch (error) {
            console.error('Kategori eklenirken hata oluştu:', error);
        }
    };

    const handleAddRequiredQuestion = async () => {
        if (!requiredQuestionText.trim() || !userId || !newlyCreatedCategoryId) {
            return;
        }

        try {
            const questionData: QuestionCreate = {
                text: requiredQuestionText.trim(),
                userId: userId,
                questionCategoryId: newlyCreatedCategoryId,
                pinId: pinId
            };

            await eventsApi.saveQuestion(questionData);

            // Modalı kapat ve state'i temizle
            setRequiredQuestionText('');
            setRequiredQuestionDialogOpen(false);

            // Kategorileri yeniden yükle
            const response = await eventsApi.getPinQuestionsAndAnswers(pinId);

            // Kategorileri ID'ye göre grupla ve tekrar edenleri filtrele
            const categoryMap = response.questions.reduce((acc, question) => {
                const category = question.questionCategory;
                if (!acc.has(category.id)) {
                    acc.set(category.id, category);
                }
                return acc;
            }, new Map<string, QuestionCategory>());

            // Map'ten array'e çevir, filtrele ve sırala
            const uniqueCategories = Array.from(categoryMap.values())
                .filter(category => category.name !== "SONUÇ VE DEĞERLENDİRME")
                .sort((a, b) => {
                    // If both indices are null, maintain original order
                    if (a.index === null && b.index === null) return 0;
                    // If only a's index is null, put it at the end
                    if (a.index === null) return 1;
                    // If only b's index is null, put it at the end
                    if (b.index === null) return -1;
                    // If both have indices, sort normally
                    return a.index - b.index;
                });

            setCategories(uniqueCategories);

            // Kategori sayılarını güncelle
            const counts = response.questions.reduce((acc, question) => {
                const categoryId = question.questionCategory.id;
                acc[categoryId] = (acc[categoryId] || 0) + 1;
                return acc;
            }, {} as { [key: string]: number });

            setCategoryQuestionCounts(counts);

            // Yeni eklenen kategorinin sorularını göster
            const categoryQuestions = response.questions.filter(
                q => q.questionCategory.id === newlyCreatedCategoryId
            );
            setQuestions(sortQuestionsStable(categoryQuestions) as unknown as Question[]);
            setSelectedCategory(newlyCreatedCategoryId);

            // Yeni eklenen kategorinin adını ayarla
            const newCategory = uniqueCategories.find(c => c.id === newlyCreatedCategoryId);
            if (newCategory) {
                setSelectedCategoryName(newCategory.name);
            }

            setNewlyCreatedCategoryId(''); // State'i temizle

        } catch (error) {
            console.error('Zorunlu soru eklenirken hata oluştu:', error);
        }
    };

    const handleAddQuestion = async () => {
        if (!newQuestionText.trim() || !userId || !selectedCategory) {
            return;
        }

        try {
            const questionData: QuestionCreate = {
                text: newQuestionText.trim(),
                userId: userId,
                questionCategoryId: selectedCategory,
                pinId: pinId
            };

            await eventsApi.saveQuestion(questionData);

            // Dialog'u kapat ve form'u temizle
            setNewQuestionText('');
            setNewQuestionDialogOpen(false);

            // Yeni soruları görebilmek için tekrar veriyi çek
            const response = await eventsApi.getPinQuestionsAndAnswers(pinId);

            // Seçili kategorinin sorularını filtrele ve güncelle
            const categoryQuestions = response.questions.filter(
                q => q.questionCategory.id === selectedCategory
            );
            setQuestions(sortQuestionsStable(categoryQuestions) as unknown as Question[]);

            // Cevapları formatla ve güncelle
            const formattedAnswers = response.questions.reduce((acc: FormAnswers, question) => {
                if (question.answers && question.answers.length > 0) {
                    const answer = question.answers[0];
                    acc[question.id] = {
                        answer: answer.yesNo ? 'evet' : 'hayir',
                        findings: answer.findings,
                        description: answer.description,
                        proposedSolutions: answer.proposedSolutions,
                        locked: true,
                        questionAnswerId: answer.id,
                        fileAttachments: answer.fileAttachments
                    };
                }
                return acc;
            }, {} as FormAnswers);
            setAnswers(formattedAnswers);

            // Kategori sayılarını güncelle
            const counts = response.questions.reduce((acc, question) => {
                const categoryId = question.questionCategory.id;
                acc[categoryId] = (acc[categoryId] || 0) + 1;
                return acc;
            }, {} as { [key: string]: number });
            setCategoryQuestionCounts(counts);

        } catch (error) {
            console.error('Soru eklenirken hata oluştu:', error);
        }
    };

    const renderContent = () => {
        if (loading) {
            return (
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '200px',
                    backgroundColor: '#fff'
                }}>
                    <Typography>Sorular yükleniyor...</Typography>
                </Box>
            );
        }

        if (!selectedCategory) {
            return (
                <Box sx={{ flexGrow: 1, mb: 3 }}>
                    <Grid container spacing={2}>
                        {categories.map((category) => (
                            <Grid item xs={12} sm={6} md={3} key={category.id}>
                                <CategoryBox
                                    name={category.name}
                                    onClick={() => handleCategoryClick(category.id)}
                                    questionCount={categoryQuestionCounts[category.id] || 0}
                                    answeredCount={categoryAnsweredCounts[category.id] || 0}
                                    canDelete={canDeleteCategories}
                                    onDelete={() => {
                                        setCategoryToDelete({ id: category.id, name: category.name });
                                        setDeleteCategoryDialogOpen(true);
                                    }}
                                />
                            </Grid>
                        ))}
                        <Grid item xs={12} sm={6} md={3}>
                            <AddCategoryBox onAddClick={() => setNewCategoryDialogOpen(true)} />
                        </Grid>
                    </Grid>
                </Box>
            );
        }

        return (
            <Box sx={{ mb: 3 }}>
                {questions.map((question, index) => (
                    <QuestionItem
                        key={question.id}
                        question={question}
                        index={index}
                        answer={answers[question.id] || defaultAnswer}
                        onAnswerChange={handleAnswerChange}
                        onDescriptionChange={handleDescriptionChange}
                        onFindingsChange={handleFindingsChange}
                        onProposedSolutionsChange={handleProposedSolutionsChange}
                        onEditClick={handleEditClick}
                        onFileChange={handleFileChange}
                        onSaveEdit={handleSaveEdit}
                        isEditing={editingQuestionId === question.id}
                        canDelete={canDeleteCategories}
                        onDelete={(id) => {
                            const q = questions.find((x) => x.id === id);
                            setQuestionToDelete({ id, text: q?.text || 'Soru' });
                            setDeleteQuestionDialogOpen(true);
                        }}
                    />
                ))}

                <Box sx={{ mb: 4 }}>
                    <Typography
                        variant="h6"
                        sx={{
                            mb: 2,
                            fontSize: '16px',
                            fontWeight: 500,
                            color: '#094151'
                        }}
                    >
                        Etkin Karşı Tedbirler Öncesi Risk ve Tehdit Seviyesi
                    </Typography>
                    <RiskTable
                        data={riskTablesData[selectedCategory]?.table1 || getEmptyRiskTableData()}
                        onChange={(field, value) => handleRiskTableChange(selectedCategory, 1, field, value)}
                        backgroundColor="#bbdefb"
                        isLocked={!!riskAnswers[selectedCategory]?.beforeMeasures}
                        isEditing={editingRiskTable.categoryId === selectedCategory && !editingRiskTable.isAfterMeasures}
                        onEdit={() => handleRiskTableEdit(selectedCategory, false)}
                        onSave={() => handleRiskTableUpdate(selectedCategory, false)}
                        isAfterMeasures={false}
                    />

                    <Typography
                        variant="h6"
                        sx={{
                            mt: 4,
                            mb: 2,
                            fontSize: '16px',
                            fontWeight: 500,
                            color: '#094151'
                        }}
                    >
                        Etkin Karşı Tedbirler Sonrası Risk ve Tehdit Seviyesi
                    </Typography>
                    <RiskTable
                        data={riskTablesData[selectedCategory]?.table2 || getEmptyRiskTableData()}
                        onChange={(field, value) => handleRiskTableChange(selectedCategory, 2, field, value)}
                        backgroundColor="#c8e6c9"
                        isLocked={!!riskAnswers[selectedCategory]?.afterMeasures}
                        isEditing={editingRiskTable.categoryId === selectedCategory && editingRiskTable.isAfterMeasures}
                        onEdit={() => handleRiskTableEdit(selectedCategory, true)}
                        onSave={() => handleRiskTableUpdate(selectedCategory, true)}
                        isAfterMeasures={true}
                    />
                </Box>

                <AddQuestionBox onAddClick={() => setNewQuestionDialogOpen(true)} />

                {/* New Question Dialog */}
                <Dialog open={newQuestionDialogOpen} onClose={() => setNewQuestionDialogOpen(false)}>
                    <DialogTitle>Yeni Soru Ekle</DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Soru"
                            fullWidth
                            multiline
                            rows={3}
                            value={newQuestionText}
                            onChange={(e) => setNewQuestionText(e.target.value)}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button
                            variant="contained"
                            onClick={() => setNewQuestionDialogOpen(false)}
                            sx={{
                                backgroundColor: '#757575',
                                color: '#ffffff',
                                '&:hover': {
                                    backgroundColor: '#616161',
                                    color: '#ffffff'
                                }
                            }}
                        >
                            İptal
                        </Button>
                        <Button
                            onClick={handleAddQuestion}
                            variant="contained"
                            sx={{
                                backgroundColor: '#094151',
                                color: '#ffffff',
                                '&:hover': {
                                    backgroundColor: '#022631',
                                    color: '#ffffff'
                                }
                            }}
                        >
                            Ekle
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        );
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: { maxHeight: '90vh' }
            }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {selectedCategory && (
                    <IconButton onClick={handleBackClick} size="small">
                        <ArrowBackIcon />
                    </IconButton>
                )}
                {selectedCategory ? selectedCategoryName : 'RİSK ANALİZ FORMU'}
            </DialogTitle>
            <DialogContent>
                <Typography sx={{ fontSize: "12px", marginBottom: "24px" }}>
                    {getInfoText()}
                </Typography>
                {renderContent()}
            </DialogContent>

            {/* Yeni Kategori Dialog'u */}
            <Dialog
                open={newCategoryDialogOpen}
                onClose={() => setNewCategoryDialogOpen(false)}
            >
                <DialogTitle>Yeni Kategori Ekle</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Kategori Adı"
                        fullWidth
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="contained"
                        onClick={() => setNewCategoryDialogOpen(false)}
                        sx={{
                            backgroundColor: '#757575',
                            color: '#ffffff',
                            '&:hover': {
                                backgroundColor: '#616161',
                                color: '#ffffff'
                            }
                        }}
                    >
                        İptal
                    </Button>
                    <Button
                        onClick={handleAddCategory}
                        variant="contained"
                        sx={{
                            backgroundColor: '#094151',
                            color: '#ffffff',
                            '&:hover': {
                                backgroundColor: '#022631',
                                color: '#ffffff'
                            }
                        }}
                    >
                        Ekle
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Kategori Silme Onay Dialog'u */}
            <Dialog
                open={deleteCategoryDialogOpen}
                onClose={() => {
                    setDeleteCategoryDialogOpen(false);
                    setCategoryToDelete(null);
                }}
            >
                <DialogTitle>Kategori Sil</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mt: 1 }}>
                        <b>{categoryToDelete?.name}</b> kategorisini silmek istediğinize emin misiniz?
                    </Typography>
                    <Typography sx={{ mt: 1, fontSize: '13px', color: '#666' }}>
                        Bu işlem sadece Admin/Süperadmin tarafından yapılabilir.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="contained"
                        onClick={() => {
                            setDeleteCategoryDialogOpen(false);
                            setCategoryToDelete(null);
                        }}
                        sx={{
                            backgroundColor: '#757575',
                            color: '#ffffff',
                            '&:hover': {
                                backgroundColor: '#616161',
                                color: '#ffffff'
                            }
                        }}
                    >
                        Vazgeç
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirmDeleteCategory}
                        sx={{
                            backgroundColor: '#b71c1c',
                            color: '#ffffff',
                            '&:hover': {
                                backgroundColor: '#7f0000',
                                color: '#ffffff'
                            },
                        }}
                    >
                        Sil
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Soru Silme Onay Dialog'u */}
            <Dialog
                open={deleteQuestionDialogOpen}
                onClose={() => {
                    setDeleteQuestionDialogOpen(false);
                    setQuestionToDelete(null);
                }}
            >
                <DialogTitle>Soru Sil</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mt: 1 }}>
                        Bu soruyu silmek istediğinize emin misiniz?
                    </Typography>
                    <Typography sx={{ mt: 1, fontSize: '13px', color: '#666' }}>
                        {questionToDelete?.text}
                    </Typography>
                    <Typography sx={{ mt: 1, fontSize: '13px', color: '#666' }}>
                        Bu işlem sadece Admin/Süperadmin tarafından yapılabilir.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="contained"
                        onClick={() => {
                            setDeleteQuestionDialogOpen(false);
                            setQuestionToDelete(null);
                        }}
                        sx={{
                            backgroundColor: '#757575',
                            color: '#ffffff',
                            '&:hover': {
                                backgroundColor: '#616161',
                                color: '#ffffff'
                            }
                        }}
                    >
                        Vazgeç
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirmDeleteQuestion}
                        sx={{
                            backgroundColor: '#b71c1c',
                            color: '#ffffff',
                            '&:hover': {
                                backgroundColor: '#7f0000',
                                color: '#ffffff'
                            },
                        }}
                    >
                        Sil
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Zorunlu Soru Dialog'u */}
            <Dialog
                open={requiredQuestionDialogOpen}
                onClose={() => { }}
                disableEscapeKeyDown
            >
                <DialogTitle>Zorunlu Soru Ekle</DialogTitle>
                <DialogContent>
                    <Typography variant="subtitle1" sx={{ mb: 2, color: '#666' }}>
                        Bu kategoriye en az bir soru eklemelisiniz.
                    </Typography>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Soru"
                        fullWidth
                        multiline
                        rows={3}
                        value={requiredQuestionText}
                        onChange={(e) => setRequiredQuestionText(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleAddRequiredQuestion}
                        variant="contained"
                        sx={{
                            backgroundColor: '#094151',
                            color: '#ffffff',
                            '&:hover': {
                                backgroundColor: '#022631',
                                color: '#ffffff'
                            }
                        }}
                    >
                        Ekle
                    </Button>
                </DialogActions>
            </Dialog>

            <DialogActions sx={{ justifyContent: 'flex-end', display: 'flex', pr: 5 }}>
                {selectedCategory && (
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        sx={{
                            width: '120px', backgroundColor: '#094151', color: "#fff",
                            '&:hover': {
                                backgroundColor: '#022631', color: "#fff"
                            }
                        }}
                    >
                        Kaydet
                    </Button>
                )}
                {!selectedCategory && (
                    <Button onClick={onClose}
                        sx={{
                            width: '220px', backgroundColor: '#094151', color: "#fff",
                            borderWidth: '2px',
                            '&:hover': {
                                backgroundColor: '#022631', color: "#fff", fontWeight: 600
                            }
                        }}>Lokasyon Sayfasına Dön</Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default memo(ChecklistModal);